import { PositionFilterCondition } from "../utils/PositionFilterCondition";
import { enumRulePairLogicCondition, QRulePair } from "./QRulePair";
import { QXMLObject } from "./QXMLObject";

export class QFilterBlock extends QXMLObject {
  constructor() {
    super();
    this.fContextId = null;
    this.fEnabled = false;
    this.fFilterRuleList = null;
    this.fName = null;
    this.fRulesLastUID = 1;
    this._cachedOrBlocks = null;
    this._cachedOrBlocksKey = "";
  }

  InitFilterBlock(name) {
    this.fEnabled = true;
    this.fFilterRuleList = [];
    this.fName = name;
  }

  LoadFromJSON(json) {
    this.fContextId = json["contextId"];
    this.fEnabled = json["enabled"] ?? true;
    this.fName = json["name"];
    this.fFilterRuleList = [];
    this.fRulesLastUID = json["rulesLastUID"] ?? 1;

    const filterRuleList = json["filterRuleList"] ?? [];
    for (let i = 0; i < filterRuleList.length; i++) {
      const filterRuleJSON = filterRuleList[i];
      const filterRule = new QRulePair();
      filterRule.LoadFromJSON(filterRuleJSON);
      this.EnsureRulesUIDs(filterRule);

      this.fFilterRuleList.push(filterRule);
    }
  }

  OutputToJSON() {
    return {
      contextId: this.fContextId,
      enabled: this.fEnabled,
      filterRuleList: this.fFilterRuleList.map(rule => rule.OutputToJSON()),
      name: this.fName,
      rulesLastUID: this.fRulesLastUID
    };
  }

  GetName() {
    return this.fName;
  }

  SetName(value) {
    this.fName = value;
  }

  GetUIDAndIncrement() {
    return this.fRulesLastUID++;
  }

  GetEnabled() {
    return this.fEnabled;
  }

  SetEnabled(enabled) {
    this.fEnabled = enabled;
  }

  GetContextId() {
    return this.fContextId;
  }

  SetContextId(ctxId) {
    this.fContextId = ctxId;
  }

  AddRule(rule) {
    this.EnsureRulesUIDs(rule);
    this.fFilterRuleList.push(rule);
  }

  AddEmptyRule(index = -1) {
    const filterRule = new QRulePair();

    filterRule.InitRulePair("", "", "", "equals", "", this.GetUIDAndIncrement());
    if (index == -1) {
      this.AddRule(filterRule);
    } else {
      this.InsertRule(filterRule, index);
    }

    return filterRule;
  }

  SetRule(idx, rule) {
    this.EnsureRulesUIDs(rule);
    this.fFilterRuleList[idx] = rule;
  }

  _getOrBlocks() {
    const cacheKey = this.fFilterRuleList
      .map(
        rule => `${rule.fUID}:${rule.fCondition}:${rule.IsValid()}:${JSON.stringify(rule.fValue)}`
      )
      .join("|");

    if (this._cachedOrBlocks === null || this._cachedOrBlocksKey !== cacheKey) {
      this._cachedOrBlocks = this.SplitIntoOrBlocks(true, false);
      this._cachedOrBlocksKey = cacheKey;
    }
    return this._cachedOrBlocks;
  }

  Matches(obj, docRef, resultOnClassMismatch = true) {
    const orBlocks = this._getOrBlocks();

    if (orBlocks.length === 0) {
      return true;
    }

    return orBlocks.every(orBlock => {
      return orBlock.some(rule => {
        return rule.PairMatch(obj, docRef, resultOnClassMismatch);
      });
    });
  }

  PositionsMatches(obj, docRef) {
    const orBlocks = this._getOrBlocks();
    const initialCondition = PositionFilterCondition.AllTrue(obj);

    if (orBlocks.length === 0) {
      return initialCondition;
    }
    const finalResult = orBlocks.reduce((acCondition, orBlock) => {
      const initialCondition = PositionFilterCondition.AllFalse(obj);
      const blockCondition = orBlock.reduce(
        (acCondition, rule) => rule.PositionsPairMatch(obj, docRef).or(acCondition),
        initialCondition
      );

      return blockCondition.and(acCondition);
    }, initialCondition);

    return finalResult;
  }

  SplitIntoOrBlocks(onlyValid = true, marshall = false, filterNull = false) {
    const allRules = this.GetRules();
    const validRules = onlyValid ? allRules.filter(rule => rule.IsValid()) : allRules;
    // TODO consider adding a check for rule.fValue !== null within the rule validation instead
    const filteredRules = filterNull ? validRules.filter(rule => rule.fValue !== null) : validRules;

    return QRulePair.SplitIntoOrBlocks(filteredRules, marshall);
  }

  Clone() {
    const clone = new QFilterBlock();
    clone.fContextId = this.fContextId;
    clone.fFilterRuleList = this.fFilterRuleList.map(rule => rule.Clone());
    clone.fName = this.fName;
    clone.fEnabled = this.fEnabled;
    clone.fRulesLastUID = this.fRulesLastUID;
    return clone;
  }

  RemoveRule(rule) {
    const index = this.fFilterRuleList.indexOf(rule);
    if (index != -1) {
      this.RemoveRuleAtIndex(index);
    }
    return index;
  }

  RemoveRuleAtIndex(index) {
    this.fFilterRuleList.splice(index, 1);
  }

  InsertRule(rule, index) {
    this.EnsureRulesUIDs(rule);
    this.fFilterRuleList.splice(index, 0, rule);
  }

  AddRules(rules) {
    this.EnsureRulesUIDs(rules);
    this.fFilterRuleList = [...this.fFilterRuleList, ...rules];
  }

  /** @returns {Array<QRulePair>} */
  GetRules() {
    return this.fFilterRuleList;
  }

  SetRules(filterRuleList) {
    this.EnsureRulesUIDs(filterRuleList);
    this.fFilterRuleList = filterRuleList;
  }

  EnsureRulesUIDs(ruleOrRules) {
    if (Array.isArray(ruleOrRules)) {
      ruleOrRules.map(rule => {
        if (!rule.fUID) rule.fUID = this.GetUIDAndIncrement();
      });
    } else {
      if (!ruleOrRules.fUID) ruleOrRules.fUID = this.GetUIDAndIncrement();
    }
  }

  Marshall(comparison = enumRulePairLogicCondition.or, filterNullValues = false) {
    const orBlocks = this.SplitIntoOrBlocks(true, true, filterNullValues);
    return orBlocks.map(orBlock => {
      return { [comparison]: orBlock };
    });
  }

  /**
   * Marshall the filter block into position and employee filter structures
   * Uses the fFieldClassName property to determine field type
   * @param {enumRulePairLogicCondition} comparison - Comparison type to use (and/or)
   * @param {boolean} filterNullValues - Whether to filter out null values
   * @returns {Object} Object with separate position and employee filters
   */
  MarshallSeparated(comparison = enumRulePairLogicCondition.or, filterNullValues = false) {
    // Get all the rules
    const allRules = this.GetRules();
    const validRules = allRules.filter(rule => rule.IsValid());
    const filteredRules = filterNullValues
      ? validRules.filter(rule => rule.fValue !== null)
      : validRules;

    // Group rules by type (Position or Box/Employee)
    const positionRules = [];
    const employeeRules = [];

    filteredRules.forEach(rule => {
      // Use fFieldClassName to determine if it's a Position or Box/Employee field
      if (rule.fFieldClassName === "Position") {
        positionRules.push(rule);
      } else {
        // Default to employee if not position (includes "Box" and any other types)
        employeeRules.push(rule);
      }
    });

    // Process position rules
    const positionFilters = this.createFiltersForRules(positionRules, comparison);

    // Process employee rules
    const employeeFilters = this.createFiltersForRules(employeeRules, comparison);

    return {
      positionFilters: positionFilters.length > 0 ? positionFilters : [],
      employeeFilters: employeeFilters.length > 0 ? employeeFilters : []
    };
  }

  /**
   * Create filter structures for a set of rules
   * @param {Array<QRulePair>} rules - Rules to process
   * @param {enumRulePairLogicCondition} comparison - Comparison type
   * @returns {Array} MongoDB-like filter structure
   */
  createFiltersForRules(rules, comparison) {
    const filters = [];
    let currentBlock = [];

    for (const rule of rules) {
      currentBlock.push(rule.Marshall());

      if (rule.fCondition === enumRulePairLogicCondition.and) {
        filters.push({ [comparison]: currentBlock });
        currentBlock = [];
      }
    }

    if (currentBlock.length > 0) {
      filters.push({ [comparison]: currentBlock });
    }

    return filters;
  }
}
